import * as React from 'react';
import { cn } from '../cn';

export interface StatTileProps {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  delta?: { value: number; label?: string };
  icon?: React.ReactNode;
  tone?: 'default' | 'accent' | 'green' | 'orange' | 'red';
  className?: string;
  href?: string;
  linkComponent?: React.ComponentType<{ href: string; className?: string; children?: React.ReactNode }>;
}
export function StatTile({ label, value, hint, delta, icon, tone = 'default', className, href, linkComponent }: StatTileProps) {
  const toneText = { default: 'text-text', accent: 'text-accent', green: 'text-green', orange: 'text-orange', red: 'text-red' }[tone];
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-text-2">{label}</span>
        {icon ? <span className="text-text-3 [&>svg]:size-4">{icon}</span> : null}
      </div>
      <div className={cn('mt-2 text-[26px] font-semibold tracking-[-0.02em] tabular leading-none', toneText)}>{value}</div>
      {delta || hint ? (
        <div className="mt-2 flex items-center gap-2 text-[12px]">
          {delta ? <span className={cn('font-medium tabular', delta.value > 0 ? 'text-green' : delta.value < 0 ? 'text-red' : 'text-text-3')}>{delta.value > 0 ? '▲' : delta.value < 0 ? '▼' : '•'} {Math.abs(delta.value)}{delta.label ? ` ${delta.label}` : ''}</span> : null}
          {hint ? <span className="text-text-3">{hint}</span> : null}
        </div>
      ) : null}
    </>
  );
  const cls = cn('block bg-surface rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] p-4', href && 'transition-[box-shadow,transform] hover:shadow-[var(--shadow)] hover:-translate-y-px', className);
  if (href) {
    const Link = linkComponent ?? (({ href, className, children }) => <a href={href} className={className}>{children}</a>);
    return <Link href={href} className={cls}>{body}</Link>;
  }
  return <div className={cls}>{body}</div>;
}

export function StatGrid({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('grid gap-3 grid-cols-2 lg:grid-cols-4', className)}>{children}</div>;
}
